import type { LightCCTV, WarningCCTV, Zone, Column, ParkingLocation, Sensor, Charger, GuideBoard, Elevator, EmergencyBell, Arrow, Line, Entrance, OnePassReader, OccupancyLight, Light, CustomObject } from '../../../../map-editor-entities/schema/exportSchema'
import type { MapObject } from '../../../../map-editor-entities/schema/types'
import { extractPosition, extractPoints, expandToRectangle, calculateBounds } from './geometryUtils'
import { extractAllRelationProperties } from './relationshipExtractor'

/**
 * properties.angle 을 export 필드로 spread.
 * 값이 number / 숫자 string 이면 보존, 아니면 omit. 회전은 적용 X (값만 저장).
 * 모든 transformer 에서 동일하게 사용 — parkingLocation 만 지원하던 angle 을
 * 전 객체 타입으로 확장하면서 추출한 공통 로직.
 */
function angleField(object: MapObject): { angle?: number } {
  const v = object.properties?.angle
  if (v == null || v === '') return {}
  const num = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(num) ? { angle: num } : {}
}

/**
 * Transform MapObject to CCTV export format
 */
export function transformToCCTV(object: MapObject): LightCCTV {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    name: object.name,
    serialNumber: object.properties?.serialNumber,
    ipAddress: object.properties?.ipAddress || '0.0.0.0',
    model: object.properties?.model,
    resolution: object.properties?.resolution,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to WarningCCTV export format.
 * 단방향 CCTV 는 모니터링 관계가 없어 LightCCTV 의 linkedParkingLocationIds /
 * linkedEmergencyBellIds 같은 필드를 갖지 않는다.
 */
export function transformToWarningCCTV(object: MapObject): WarningCCTV {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    name: object.name,
    ipAddress: object.properties?.ipAddress || '0.0.0.0',
    ...angleField(object),
    ...relationProps,
  }
}

/**
 * Transform MapObject to Zone export format
 */
export function transformToZone(object: MapObject): Zone {
  // Try to extract points if available, otherwise create from position/size
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
  } catch {
    // Fallback: create rectangle from center position if stored in properties
    const pos = object.properties?.position || { x: 0, y: 0 }
    const size = object.properties?.size || { width: 100, height: 100 }

    points = [
      [pos.x, pos.y],
      [pos.x + size.width, pos.y],
      [pos.x + size.width, pos.y + size.height],
      [pos.x, pos.y + size.height]
    ]
  }

  const relationProps = extractAllRelationProperties(object)
  const position = extractPosition(object.geometry)

  return {
    id: String(object.id),
    // 콘솔/router 는 구역 번호만 쓴다 ('1구역'→'1')
    name: object.name.replace(/\s*구역$/, ''),
    position,
    points,
    ...calculateBounds(points),
    zoneName: object.properties?.zoneName,
    capacity: object.properties?.capacity,
    handicappedSpots: object.properties?.handicappedSpots,
    includedParkingLocationIds: object.properties?.includedParkingLocationIds ?? [],
    ...angleField(object),
    ...relationProps
  }
}

/**
 * Transform MapObject to Column export format
 */
export function transformToColumn(object: MapObject): Column {
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
    if (points.length < 3) {
      points = expandToRectangle(points)
    }
  } catch {
    const pos = object.properties?.position || { x: 0, y: 0 }
    const size = object.properties?.size || { width: 20, height: 20 }
    points = [
      [pos.x, pos.y],
      [pos.x + size.width, pos.y],
      [pos.x + size.width, pos.y + size.height],
      [pos.x, pos.y + size.height]
    ]
  }

  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points,
    ...calculateBounds(points),
    label: object.properties?.label ?? object.name,
    ...angleField(object),
    ...relationProps
  }
}

const PARKING_TYPE_VALUES = ['REGULAR', 'HANDICAPPED', 'WOMEN_ONLY', 'COMPACT', 'ELECTRIC', 'FAMILY', 'VULNERABLE', 'MOTORCYCLE'] as const

function normalizeParkingType(value: unknown): typeof PARKING_TYPE_VALUES[number] {
  const s = typeof value === 'string' ? value.toUpperCase() : ''
  return (PARKING_TYPE_VALUES as readonly string[]).includes(s) ? (s as typeof PARKING_TYPE_VALUES[number]) : 'REGULAR'
}

/**
 * Transform MapObject to ParkingLocation export format.
 * Always exports `type` (required by router/DB); prefers properties.type then properties.spotType.
 */
export function transformToParkingLocation(object: MapObject): ParkingLocation {
  // Try to extract points if available, otherwise create from position/size
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
    if (points.length < 3) {
      points = expandToRectangle(points)
    }
  } catch {
    // Fallback: create rectangle from center position if stored in properties
    const pos = object.properties?.position || { x: 0, y: 0 }
    const size = object.properties?.size || { width: 50, height: 30 }

    points = [
      [pos.x, pos.y],
      [pos.x + size.width, pos.y],
      [pos.x + size.width, pos.y + size.height],
      [pos.x, pos.y + size.height]
    ]
  }

  const relationProps = extractAllRelationProperties(object)
  const typeValue = normalizeParkingType(object.properties?.type ?? object.properties?.spotType ?? 'REGULAR')
  const position = extractPosition(object.geometry)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points,
    ...calculateBounds(points),
    type: typeValue,
    // parkingLocation 은 historically 0 default. helper 와 다르게 항상 값 보장.
    angle: angleField(object).angle ?? 0,
    spotNumber: object.properties?.spotNumber,
    ...(object.properties?.spotType != null && { spotType: object.properties.spotType }),
    status: object.properties?.status,
    monitoringCctvs: object.properties?.monitoringCctvs ?? [],
    ...relationProps
  }
}

/**
 * Transform MapObject to Sensor export format
 */
export function transformToSensor(object: MapObject): Sensor {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    sensorType: object.properties?.sensorType,
    model: object.properties?.model,
    range: object.properties?.range,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to Charger export format.
 *
 * 전기 충전기는 router DTO 가 { name, position, points } 만 처리한다.
 * id/name/position 만 의미 있고 width/height/powerOutput/chargingType 등
 * 부가 필드는 백엔드 처리 경로가 없어서 항상 0 / undefined 였다 — 출력에서
 * 제외해 카메라/주차구역 등 다른 객체 payload 와 톤을 맞춘다.
 * points 는 ChargerSchema 가 required 라 단일점으로 채워준다.
 */
export function transformToCharger(object: MapObject): Charger {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    ...relationProps,
  }
}

/**
 * Transform MapObject to GuideBoard export format
 */
export function transformToGuideBoard(object: MapObject): GuideBoard {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    ipAddress: object.properties?.ipAddress || '0.0.0.0',
    content: object.properties?.content,
    direction: object.properties?.direction,
    leftDirectionIds: object.properties?.leftDirectionIds ?? [],
    rightDirectionIds: object.properties?.rightDirectionIds ?? [],
    ...angleField(object),
    ...relationProps
  }
}

/**
 * Transform MapObject to Elevator export format
 */
export function transformToElevator(object: MapObject): Elevator {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    floors: object.properties?.floors,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to EmergencyBell export format
 */
export function transformToEmergencyBell(object: MapObject): EmergencyBell {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    linkedCctvId: object.properties?.linkedCctvId ?? '',
    linkedCustomId: object.properties?.linkedCustomId ?? '',
    ...angleField(object),
    ...relationProps
  }
}

/**
 * Transform MapObject to Arrow export format
 */
export function transformToArrow(object: MapObject): Arrow {
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
  } catch {
    const pos = object.properties?.position || { x: 0, y: 0 }
    points = [
      [pos.x, pos.y],
      [pos.x + 50, pos.y]
    ]
  }

  const relationProps = extractAllRelationProperties(object)
  const position = extractPosition(object.geometry)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points,
    ...calculateBounds(points),
    direction: object.properties?.direction,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to Line export format (for OutLine/InnerLine)
 */
export function transformToLine(object: MapObject): Line {
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
  } catch {
    const pos = object.properties?.position || { x: 0, y: 0 }
    points = [
      [pos.x, pos.y],
      [pos.x + 100, pos.y]
    ]
  }

  const relationProps = extractAllRelationProperties(object)
  const position = extractPosition(object.geometry)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points,
    ...calculateBounds(points),
    lineType: object.properties?.lineType,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to Entrance export format
 */
export function transformToEntrance(object: MapObject): Entrance {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    entranceType: object.properties?.entranceType,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to OnePassReader export format
 */
export function transformToOnePassReader(object: MapObject): OnePassReader {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    readerId: object.properties?.readerId,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to OccupancyLight export format
 */
export function transformToOccupancyLight(object: MapObject): OccupancyLight {
  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points: [[position[0], position[1]]] as Array<[number, number]>,
    width: 0,
    height: 0,
    status: object.properties?.status,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to Light export format
 */
export function transformToLight(object: MapObject): Light {
  let points: Array<[number, number]>

  try {
    points = extractPoints(object.geometry)
    if (points.length < 3) {
      points = expandToRectangle(points)
    }
  } catch {
    const pos = object.properties?.position || { x: 0, y: 0 }
    const size = object.properties?.size || { width: 10, height: 10 }
    points = [
      [pos.x, pos.y],
      [pos.x + size.width, pos.y],
      [pos.x + size.width, pos.y + size.height],
      [pos.x, pos.y + size.height]
    ]
  }

  const position = extractPosition(object.geometry)
  const relationProps = extractAllRelationProperties(object)

  return {
    id: String(object.id),
    name: object.name,
    position,
    points,
    ...calculateBounds(points),
    lightType: object.properties?.lightType,
    ...angleField(object),
    ...relationProps // Include all relationship properties
  }
}

/**
 * Transform MapObject to CustomObject export format.
 *
 * 표준 카테고리로 라우팅되지 않는 사용자 정의 객체(예: 'dongs' 동 라벨)용.
 * 좌상단 position + 바운딩박스 size + 꼭지점 points 를 일관되게 산출한다.
 * - polygon/polyline: 실제 정점을 points 로, 정점 bbox 로 position/size 계산
 * - point(라벨/아이콘): geometry 좌표는 element 중심이므로 properties.__size 로
 *   좌상단을 역산하고 사각형 4꼭지점을 만든다
 */
export function transformToCustomObject(object: MapObject): CustomObject {
  const relationProps = extractAllRelationProperties(object)
  const sizeProp = object.properties?.__size as { width?: number; height?: number } | undefined

  let points: Array<[number, number]>
  let position: [number, number]
  let width: number
  let height: number

  if (object.geometry.type === 'polygon' || object.geometry.type === 'polyline') {
    points = extractPoints(object.geometry)
    const bounds = calculateBounds(points)
    width = bounds.width
    height = bounds.height
    const xs = points.map(p => p[0])
    const ys = points.map(p => p[1])
    position = [Math.min(...xs), Math.min(...ys)]
  } else {
    // point geometry: coordinates 는 element 중심점
    const center = extractPosition(object.geometry)
    width = sizeProp?.width ?? 0
    height = sizeProp?.height ?? 0
    const x = center[0] - width / 2
    const y = center[1] - height / 2
    position = [x, y]
    points = [
      [x, y],
      [x + width, y],
      [x + width, y + height],
      [x, y + height],
    ]
  }

  return {
    id: String(object.id),
    name: object.name,
    layer: object.layer,
    type: object.type,
    position,
    size: { width, height },
    points,
    ...angleField(object),
    ...relationProps,
  }
}
